import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface TOCItem {
  id: string;
  label: string;
}

interface ArticleData {
  date: string;
  updatedDate: string;
  readingTime: string;
  title: string;
  subtitle: string;
  author: string;
  heroImageUrl: string;
  tableOfContents: TOCItem[];
}

interface RelatedArticle {
  title: string;
  desc: string;
  link: string;
  isReady: boolean;
}

interface ArticleLayoutProps {
  article: ArticleData;
  relatedArticles: RelatedArticle[];
  children: React.ReactNode;
}

export default function ArticleLayout({ article, relatedArticles, children }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article>
        <header className="relative pt-20 md:pt-24 bg-background-100 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.heroImageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-background-100"></div>
          </div>
          <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 py-16 md:py-24 text-center">
            <div className="flex items-center justify-center gap-3 text-xs text-white/70 mb-4">
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readingTime}</span>
              <span>·</span>
              <span>Aktualisiert: {article.updatedDate}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-6">
              {article.subtitle}
            </p>
            <div className="flex items-center justify-center gap-6 text-xs text-white/60">
              <span>Von: {article.author}</span>
              <span>Geprüft: {article.updatedDate}</span>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-24 bg-white rounded-2xl p-5">
                <h4 className="font-semibold text-sm text-foreground-900 mb-4">Inhaltsverzeichnis</h4>
                <nav>
                  <ul className="space-y-2">
                    {article.tableOfContents.map((item, idx) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className="text-xs text-foreground-500 hover:text-primary-600 transition-colors block py-1"
                        >
                          {idx + 1}. {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
                <div className="mt-6 pt-4 border-t border-background-200">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Zurück zur Startseite
                  </Link>
                </div>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="space-y-10 md:space-y-14">
                {children}
              </div>

              <div className="mt-12 pt-8 border-t border-background-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-xs text-foreground-400">Letzte Aktualisierung: {article.updatedDate}</p>
                    <p className="text-xs text-foreground-400">Geprüft von: {article.author}</p>
                  </div>
                  <div className="flex gap-2">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-700 text-xs font-medium rounded-full hover:bg-background-200 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-home-4-line"></i>
                      Startseite
                    </Link>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-white text-xs font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      Zum Spreadsheet
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
            Verwandte Artikel
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {relatedArticles.map((item, idx) => (
              item.isReady ? (
                <Link
                  key={idx}
                  to={item.link}
                  className="bg-white rounded-2xl p-5 border border-background-200/70 hover:border-primary-200 hover:scale-[1.02] transition-all cursor-pointer group"
                >
                  <span className="inline-block px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">Artikel</span>
                  <h4 className="font-semibold text-sm text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors">{item.title}</h4>
                  <p className="text-xs text-foreground-500">{item.desc}</p>
                </Link>
              ) : (
                <div key={idx} className="bg-white rounded-2xl p-5 border border-background-200/70">
                  <span className="inline-block px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-3">Bald</span>
                  <h4 className="font-semibold text-sm text-foreground-900 mb-1">{item.title}</h4>
                  <p className="text-xs text-foreground-500">{item.desc}</p>
                </div>
              )
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}